import { useEffect } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import {
  WORKAROUND_TYPES,
  WORKAROUND_DISPLAY_LABELS,
} from '../../../../../../shared/types/business-functions-workarounds';

type UseSyncRuntimeHoursProps = {
  workaroundNeeded: boolean;
  workaroundDefined: string;
  runtimeHours: number | string | null | undefined;
  setValue: UseFormSetValue<any>;
};

/**
 * @description
 * This hook manages the synchronization of runtime hours values in the Business Function Workarounds form.
 *
 * @businessContext
 * In the context of business continuity planning, this hook ensures that:
 * 1. When a workaround is not needed, all workaround-related fields are reset to default values
 * 2. When a workaround is needed, the runtime hours field is properly synchronized based on the selected workaround type:
 *    - For "Infinite" workarounds: Sets runtime to "Infinite" (displayed as "Infinite hours")
 *    - For "No Workaround" option: Sets runtime to "N/A" (displayed as "N/A")
 *    - For "Defined Time" workarounds: Preserves user input for specific runtime hours
 *
 * This synchronization ensures that the runtime hours value always correctly reflects the selected workaround strategy,
 * which is critical for accurate business impact analysis and recovery planning.
 *
 * @param workaroundNeeded - Indicates whether a workaround is needed for the business function
 * @param workaroundDefined - The type of workaround defined (INFINITE, DEFINED_TIME, NO_WORKAROUND)
 * @param runtimeHours - The current runtime hours value
 * @param setValue - Function to update form values
 *
 * @example
 * When a user selects "No Workaround", the hook will set runtimeHours to "N/A"
 * When a user selects "Infinite", the hook will set runtimeHours to "Infinite"
 * When a user selects "Defined Time" and enters "48", the hook will preserve "48"
 */
export const useSyncRuntimeHours = ({
  workaroundNeeded,
  workaroundDefined,
  runtimeHours,
  setValue,
}: UseSyncRuntimeHoursProps) => {
  useEffect(() => {
    if (!workaroundNeeded) {
      const defaultValues = {
        workaroundDefined: WORKAROUND_TYPES.NO_WORKAROUND,
        runtimeHours: WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND],
        maxDataLossHours: 0,
        workaroundDescription: '',
      };

      Object.entries(defaultValues).forEach(([key, value]) => {
        setValue(key as keyof typeof defaultValues, value);
      });

      return;
    }
    const isPredefinedWorkaround =
      workaroundDefined === WORKAROUND_TYPES.INFINITE ||
      workaroundDefined === WORKAROUND_TYPES.NO_WORKAROUND;

    const isDefinedTimeWithWrongRuntime =
      workaroundDefined === WORKAROUND_TYPES.DEFINED_TIME &&
      (runtimeHours ===
        WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND] ||
        runtimeHours === WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.INFINITE]);

    if (isPredefinedWorkaround) {
      setValue('runtimeHours', WORKAROUND_DISPLAY_LABELS[workaroundDefined]);
      return;
    }

    if (isDefinedTimeWithWrongRuntime) {
      setValue(
        'runtimeHours',
        WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND],
      );
      return;
    }

    if (runtimeHours === -1) {
      setValue(
        'runtimeHours',
        WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.INFINITE],
      );
      return;
    }

    if (runtimeHours == null || runtimeHours === '') {
      setValue(
        'runtimeHours',
        WORKAROUND_DISPLAY_LABELS[WORKAROUND_TYPES.NO_WORKAROUND],
      );
    }
  }, [workaroundNeeded, workaroundDefined, runtimeHours, setValue]);
};
